import React from 'react';
import { Volume2, ChevronRight, Home, Castle, Mail, Heart, Landmark, BookOpen } from 'lucide-react';
import { ArchivalImageCard } from '../ArchivalImageCard';
import { useVoiceReader } from '../../context/VoiceReaderContext';

interface RobertsPart3SectionProps {
  onSelectPage?: (pageId: string) => void;
}

export const RobertsPart3Section: React.FC<RobertsPart3SectionProps> = ({ onSelectPage }) => {
  const { speakCurrentPage, isPlaying } = useVoiceReader();

  return (
    <div className="space-y-10 text-[#2c221e] font-sans">
      {/* Top Breadcrumb / Page Header */}
      <header className="bg-[#f3ede0] p-6 sm:p-10 md:p-12 rounded-3xl border-2 border-[#d8ceb9] shadow-sm space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 text-xs font-serif text-[#7a5b3a]">
          <div className="flex items-center gap-1.5 font-bold">
            {onSelectPage && (
              <button
                onClick={() => onSelectPage('hub')}
                className="hover:underline flex items-center gap-1 text-[#7a5b3a] cursor-pointer"
              >
                <Home className="w-3.5 h-3.5" />
                <span>The Roberts Family</span>
              </button>
            )}
            {onSelectPage && <ChevronRight className="w-3.5 h-3.5 text-[#d8ceb9]" />}
            <span className="bg-[#e8dec9] px-2.5 py-1 rounded-full uppercase tracking-wider text-[11px] border border-[#d8ceb9]">
              Part 3 of 5
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={speakCurrentPage}
              className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white text-xs font-bold transition shadow-xs cursor-pointer"
              title="Listen to Part 3: Ted (Edward) Roberts and his family"
            >
              <Volume2 className={`w-3.5 h-3.5 ${isPlaying ? 'animate-pulse text-amber-200' : 'text-amber-300'}`} />
              <span>{isPlaying ? 'Listening to Part 3...' : 'Listen to Part 3'}</span>
            </button>
          </div>
        </div>

        <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-extrabold text-[#2c221e] tracking-tight">
          TED (EDWARD) ROBERTS (1881-1975) and his family
        </h1>
        <p className="font-serif text-base sm:text-lg text-[#7a5b3a] italic leading-relaxed">
          Riverside at Culverden, Romance, Marriage and a Honeymoon, "A Personal History", Edward T. Roberts and Over a Century of Change in Farming Practice
        </p>
      </header>

      {/* Main Archival Article */}
      <article className="bg-[#fbf9f4] p-6 sm:p-10 md:p-12 rounded-3xl border border-[#d8ceb9] shadow-sm space-y-8 text-base leading-relaxed text-[#2c221e]">
        
        {/* Intro */}
        <section className="space-y-6">
          <p>
            Edward, known as Ted, was a man of the land and of word in verse and narrative, although at one stage of his life he wanted to become a schoolteacher. His father discouraged him, including the warning that the wrath of parents would not be something one would want to bear.
          </p>

          <p>
            His son, Robert Charles Edward Trueman Roberts who used the name Edward, wrote under the name Edw. T. Roberts, and to his community he was known as someone who lived to the highest ideals of service and integrity. Their combined legacy in word and deed is very highly valued, passed down with pride through the generations.
          </p>

          <p>
            His three daughters from his marriage to Eva Fanny Rinaldi - Margaret, Helen and Ailsa - all contributed to their communities, through careers and organisations, and their families have all enjoyed high levels of achievement in business and academia.
          </p>

          <p>
            Ted Roberts<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-1">1</a></sup> married Jenny (Sarah Jane) Trueman<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-2">2</a></sup> at Blind River, Marlborough in 1907. Their tragedy was that Jenny died after childbirth, aged 34, of cerebral hemorrhage. The baby's fortune was that he was able to grow up on Riverside, Culverden, in the loving care of his father and Aunt Margaret for his first 15 years, when he got a kind stepmother and subsequently, three half-sisters.
          </p>

          <p>
            Ted wrote about his summer romance with Jenny in "A Personal History". He was living in Marlborough with his brother and sister-in-law, Morris and Phoebe Roberts, at Blind River.
          </p>
        </section>

        {/* Section: ROMANCE, MARRIAGE AND A HONEYMOON */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <div className="flex items-center gap-2 text-[#7a5b3a]">
            <Heart className="w-5 h-5" />
            <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
              ROMANCE, MARRIAGE AND A HONEYMOON<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-3">3</a></sup>
            </h2>
          </div>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p>
              "During the harvest Jenny Trueman and I became friendly. Her mother died in 1905 while George and I were still in Southland<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-4">4</a></sup> and she had been running the home and attending to her father and brother for some time before her mother's death, as Mrs Trueman was ill for several months before the end came...... I was a frequent visitor after harvest to the Trueman home and on March 1st 1906 became engaged to Jenny. Next day she left for a holiday in the North Island. May Horn of Seddon joined her..... Shortly after Jenny returned home I gave her an engagement ring that I bought from a jeweller in Christchurch for five pounds. On a visit to Blenheim we had our photos taken by McCusker and I sent one to Mr George Sheat of Morton Mains, one to Mr and Mrs S.B. Gibb and one to my parents at Scargill. In 1906 I made my home with Morris and Phebe, but sometimes helped Eddy Trueman to cart chaff to Seddon and load it on trucks.....
            </p>

            <p>
              Jenny Trueman and I decided to get married on January 20th 1907, and then visit the Christchurch Exhibition and my relatives in Scargill and Motunau. Mr Howell, curate in Blenheim came out on the train and someone drove him to Mr Trueman's home at Blind River, where the marriage service was held. Miss May Holm of Seddon was bridesmaid and Eddie Trueman best man, Mrs Walker of Seddon was engaged for the day to help with refreshments. The spread was put on a long table in the barn which Eddie and I tried to decorate with branches from native shrubs that grew along the banks of the Blind River.
            </p>
          </blockquote>

          {/* Image 205 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/205 Ted and Jennie_s engagement photo.jpg"
              alt="Edward and Jennie (Sarah Jane) Roberts in their engagement photo"
              caption="Edward and Jennie (Sarah Jane) Roberts in their engagement photo"
              filenameHint="205 Ted and Jennie_s engagement photo.jpg"
            />
          </figure>

          <p>
            Some friends of the Trueman family came up from the Picton Road and Mr and Mrs Joe Allan came from Richmond Brook. My brother Morris and Phoebe were guests, even though their son Arthur Gordon had been born about ten days earlier. We left Seddon on the evening train for Picton. As the train pulled away from the station there were some loud bangs from detonators that had been placed on the line as a mark of goodwill to the newly married couple. As there was a ship sailing for Wellington that Wednesday evening 30 January, we took lodgings in Mrs Alport's boarding house, and sailed the next evening and remained on the ship where we had breakfast next morning before leaving. The day we spent in Wellington was rather unpleasant, owing to a strong nor-wester wind.
          </p>

          {/* Image 206 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/206 Maroroa.jpg"
              alt="Mararoa"
              caption="Mararoa"
              filenameHint="206 Maroroa.jpg"
            />
          </figure>

          <p>
            Next evening we sailed on the Mararoa for Lyttelton. Extra beds were arranged on the ship to provide for the people travelling to Christchurch to visit the Exhibition. On reaching Christchurch we put up at the Trocodero in lower Manchester Street, under the management of a Mr David. The charge was six shillings a day....
          </p>

          {/* Image 207 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/207 NZ Exhibition.jpg"
              alt="NZ Exhibition"
              caption="NZ Exhibition"
              filenameHint="207 NZ Exhibition.jpg"
            />
          </figure>

          <p>
            After spending a few days in Christchurch and visiting the Exhibition we went to Scargill by train. Father and Mother were at the station with a horse and buggy to meet us and drive us home. Their wedding present was a large Family Bible....... There was an organ in the Plas Isa home and a good part of the afternoon was spent singing. Father had a garden under a bank some distance away and was busy in it. He thought about coming home but did not wish to do so until the visitors had left. Now and again he would walk up the bank and listen to the sound of music and singing. When at last it ceased he came home. It was not that he did not appreciate music and singing but did not want to waste time in working hours. We had the use of the horse and buggy while at Plas Isa and spent a night up at Glen Clova and Heathfield......
          </p>

          <p>
            On our return journey to Christchurch Mr Jack Mathers met the train at Sefton and drove us to Salt Water Creek. He had married Kate Armstrong some time after 1903 and when we were there in February 1907 they had one son Jock just old enough to amuse himself by banging away on the piano. Next evening Mr Mathers drove us to Sefton, where we intended to stay with Uncle Jim and Aunt Fanny (Mr and Mrs Pain), but Uncle Jim was ill with stomach flu, so we went on to Christchurch on the evening train and put up at the Trocodero. We travelled to Wellington on the SS Pataena, and most of the passengers were Maris returning to the North Island after visiting the Exhibition. We had to take another ship to get to Picton and we probably stayed at Alport's Boarding House in Picton for the night and left by the morning train for Seddon. On the Blenheim platform we met Mr Fred Bursil of Sedgemere Blind River and he congratulated me on my marriage.
          </p>

          {/* Image 208 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/208 Blenheim Railway Station.jpg"
              alt="Blenheim Railway Station"
              caption="Blenheim Railway Station"
              filenameHint="208 Blenheim Railway Station.jpg"
            />
          </figure>

          <p>
            My brother Morris met us at Seddon and drove us to his home, where Phoebe gave us a good welcome and had dinner prepared for us. After a good meal and talk Morris drove us to Mr Trueman's farm, which he called Collingbourne after the vicarage in Wiltshire where he was born. It was to be our home until I drew a 709 acre section near Culverden<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-5">5</a></sup> 26 June 1908. Jenny did the housekeeping for her father, brother and me free of charge in return for food and shelter. When I did work for Mr Trueman he paid me and when I was not working I got all the work I could do either from the settlers nearby or on public works."
          </p>

          <p className="italic text-[#5c4328]">
            Little could they have known they would have a married life of only 75 months; Ted stayed close to the Trueman family for the rest of his life.
          </p>
        </section>

        {/* Section: GETTING RIVERSIDE */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            GETTING RIVERSIDE
          </h2>

          <p>
            In 1908 Ted was working for the Public Works Department helping put up telephone poles along the railway line under construction south of Seddon. One of the overseers had a map of Culverden and the 29 sections that were to go to ballot, and he discussed them with Ted. Another one of the railway workers, Jack Devine from Rotherham, reported that he had seen large yields on sections 1, 2 and 4 that lay just south of the rabbit fence near the Waiau River. That gave Ted the idea of applying for section 2 of 709 acres. Meantime, his brother George had spoken to someone who had been the government valuer for the Culverden Estate and was told that sections 5 and 6 were good sections to apply for. No 5 section was nearly 900 acres and all in native cover.
          </p>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-base leading-relaxed text-[#2c221e] space-y-4">
            <p>
              "So I applied for sections 2 and 5. My wife was allowed to apply for the same two sections as I. The Land Board sat in Blenheim to examine applicants as to their experience as farmers and to the amount of capital they held or could obtain. Jenny and I between us had 400 Pounds and Mr Trueman agreed to lend us 300 Pounds." They then had to get proof that the money could be raised as promised and that was quite a run around but finally the BNZ in Blenheim came through on the paperwork. "I wrote to Father asking him to attend the ballot in Christchurch on 26 June 1908 for the Culverden sections and if I drew one of the two sections applied for then he was to cancel the application for the other, Jenny's applications as well as mine. We read in the Marlborough Express that the applications for the sections numbered over 600 or an average of about 20 per section so the night before the ballot we decided at Blind River to have a mock ballot. We put 16 blanks in a hat, Jenny put two papers with her name on them and I put in two with my name on them. After mixing the papers well Jenny put her hand in and drew out a blank. Then I had a draw and drew out my own name, which we took to be a good omen of success."
            </p>
          </blockquote>

          <p>
            Next afternoon the postmistress from Blind River arrived at the Trueman house with a telegram from Robert Morris Roberts in Christchurch - "You have drawn section 2."
          </p>

          <p>
            By July 3 Ted was on his way to Culverden to see what his future looked like. "On arrival at Scargill I was told my brother Tom was on the rifle range which was on the property of Mr George Greig which lay between the Waikari and Scargill streams. Tom's wife Chrys whom I was to meet for the first time was one the range and kissed me. I rode my bike and took my dog to Plas Isa and stayed with Tom and Chrys who were married in the North Island in the autumn."
          </p>

          <p>
            "Next day was Sunday and, in the afternoon, which was bleak, I rode my bike over to the Flat and called on Mrs Gibb, George, and his sister Ella, and then on to Glen Clova. My sister Margaret was there assisting my sister Jenny with the housework and the children." Those children were Burnie, Mildred, Greta, George, Alice, Edward and Olive.
          </p>

          <p>
            He finally got to his section. "On arriving at my section I found John Munro, the stock inspector of Rotherham, and Monty Sams, the rabbit inspector for the Hurunui Rabbit Board. They had a greyhound and were out to catch hares. I walked round the river boundary of the section and concluded I would have to fence it to keep the stock on the 329 acres of native pasture outside the rabbit fence. "That fencing was not to be completed for many years as Ted used temporary fencing to keep his sheep in.
          </p>

          <p>
            On Tuesday morning he met his neighbour Joe Thomson, and they inspected their boundary, agreeing to fence it themselves, using birch fence posts from Waiau.
          </p>

          <p>
            On the Wednesday of that momentous week he caught the train to Christchurch, where his father met him at the station and they went by tram, for two pence each, to St Albans where Robert and Sarah were living in retirement.
          </p>

          <p>
            "Next day I went to the Government Stock Department and registered an ear-mark, a notch in the front of the left ear and a split I the same ear. The brand I was allowed was YT." Bill Shadrack the blacksmith, whose wife ran the post office, made his sheep brand - and another milestone was passed. Now he needed sheep and he asked George Gibb to advise him on what sheep to buy at the Cheviot sheep sale, and at the Addington sheep sales the next day, for which they took the morning train. He got three trucks of sheep, mostly old ewes but also some hoggets.
          </p>

          <p>
            Thursday, the same day that he bought the sheep George Gibb took him to the BNZ in Hereford Street and introduced him to the manager, and he opened his farm account. And on the Sunday he went to the Anglican Church Service at the Greta Valley School. And after church he set off back to Blind River - now a landowner with stock and a banker!
          </p>

          <p>
            Jenny arrived on 1 September 1908 and Ted was pleased that from then on the meals improved! In November 1908 Mr Trueman came down by coach, a three-day journey from Blind River to Culverden. Ted met him at Rotherham and drove him to see the new three-room house being built, and which was finished in May 1901. Mr Trueman gave his daughter a gift of a Carl Wagner piano, costing 45 guineas. It was a task to get the piano from Culverden, by spring dray to the farm, and then unloading the piano by running the wheels of the dray into hollows and a lot of heavy lifting.
          </p>

          <p>
            Sheep yards were built in 1909, and a small sheep dip the following year. In July 1910 he started to plough and so their farming life at Riverside started - the story of Riverside continues shortly.
          </p>
        </section>

        {/* Section: APRIL 1913 */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            APRIL 1913
          </h2>

          <p>
            In 1913 Jenny's first pregnancy was nearly over. On 3 April, Ted took Jenny by train to Christchurch where they were met by Stanley Grimson<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-6">6</a></sup> who had a cab ordered to take them to his home in Sydney Street, Spreydon where Jenny was to stay before she went into the maternity home run by Mrs Bethune, at 6 Cashel Street. They did not stay with his mother Sarah Roberts as she was in a private hospital recovering from an operation for a blocked bowel.
          </p>

          <div className="space-y-4">
            <h3 className="font-serif text-xl font-bold text-[#2c221e]">
              Ted's account
            </h3>

            <blockquote className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
              <p>
                "I went home on Monday 7 April. Annie Gibb who we left in charge was at the station to meet me. The following Monday 14 April a telegram came in Gallagher's mail bag from Mrs Bethune saying a son born and both well. When I told Annie she remarked "It is good that is over."
              </p>

              <p>
                Next morning I went out with the team and commenced to plough a paddock and soon saw a motor car approaching with two men in it. They were insurance agents and the postmaster at Culverden had given them a telegram to deliver to me. It read: Mrs Roberts seriously ill. Come at once." Signed Bethune. The men said they would drive me to Christchurch for five pounds, so after attending to the horses and changing my clothes we left for the city. At Culverden I sent a telegram to Mrs Trueman, Blind River, reading "Jenny seriously ill at 6 Cashel Street, Christchurch."
              </p>

              <p>
                After writing a cheque for Dalgety & Co on arrival at the nursing home and handing it to the men who drove me down, I was shown into a single room where Jenny was lying unconscious and stayed with her until she passed away soon after midnight or early Wednesday morning 16 April.
              </p>

              <p>
                Father called in the evening of Tuesday as did Dr De Renzy who attended the confinement. Mrs Bethune rang for a taxi to take me to 137 Bealey Street where my sister Margaret was waiting up expecting me. Next morning, I sent Mr Trueman a telegram informing him Jennie had passed away. He came down by sea for the funeral and I went to Lyttelton in the evening after it and saw him on the ferry steamer.
              </p>

              <p>
                The burial service took place at the grave side in the Linwood Cemetery<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-7">7</a></sup>. Mrs Buthane took the baby to his baptismal service at St Luke's in Manchester Street by Mr Sedgewick, who later became Bishop of Waiapu. The baby remained in the home about a month and then my sister Margaret, who was living with our parents, took charge of Robert Charles Edward Trueman as the baby was named. Annie Gibb remained with me and ran the house very efficiently."
              </p>
            </blockquote>
          </div>

          {/* Image 209 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/209 Maggie and Edward T. in 1933.jpg"
              alt="Maggie and Edward T. in 1933"
              caption="Aunt Maggie and Edward T. in 1933 - Ted's sister Maggie lived with Ted and Edward T. for 15 years. Edward T and his Aunt Maggie pictured in 1933 (Photo: R L Roberts)"
              filenameHint="209 Maggie and Edward T. in 1933.jpg"
            />
          </figure>

          <p>
            On 3 October 1928 Ted married a talented woman, Eva Fanny Rinaldi<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-8">8</a></sup> of Rotherham. Eva (1898 1996) was born at West Eyreton, the eldest daughter of Edward and Margaret Rinaldi. The family moved to Clifden, 8 km from Waiau School, in 1903. When WW1 Started in 1914 she joined the Red Cross and was an active member for the next 75 years. Two of her five brothers, Bert and Sid, were killed in that war. When the family oved to Christchurch Eva trained as a dressmaker and tailoress, and worked as a dressmaker for Farmers Department Store, and made beautiful wedding dresses for family and friends. She taught at Sunday School, and enjoyed photography, like her stepson Edward T.
          </p>

          <p>
            In 1923 the family moved to a farm near Rotherham- it was in that district that she met and married Ted Roberts, when she was 30. They had three daughters, Elizabeth Margaret (Fordyce), Helen Evangeline (Ford), and Ailsa Catherine (Fraser), giving Edward T. the joy of having three much younger half-sisters.
          </p>

          <p>
            Eva and Ted attended St Mark's Church in Rotherham, where Eva played the organ for many years. She was active in many community organisations. Ted and Eva retired to Kendal Avenue, Christchurch in 1968 and Eva lived there after Ted's death in 1975 until the last 18 months of her life, when she resided at Bishop Selwyn Life Care on Park Avenue, Christchurch.
          </p>

          {/* Image 210 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/210 Ailsa, Helen and Margaret Roberts.jpg"
              alt="Ailsa, Helen and Margaret Roberts"
              caption="L-L: Ailsa, Helen and Margaret Roberts"
              filenameHint="210 Ailsa, Helen and Margaret Roberts.jpg"
            />
          </figure>

          <div className="space-y-4">
            <p>
              Margaret became a teacher, and married lan Fordyce. They had a family of high achievers.
              <br />Ewan had a distinguished academic career in the field of geology.
              <br />Alastair, was a systems and architecture engineer.
              <br />Neil was also in the field of electrical engineering.
              <br />Alison had a career in education.
              <br />David combined agribusiness with farming, and loved his years working at Willowbank Wildlife Park.
            </p>

            <p>
              Helen also went teaching, and married artist John Bevan Ford, a pioneer of contemporary Māori art, and the first NZ artists to present a series of lectures about his work at the Metropolitan Museum of Art in New York. They later divorced.
            </p>

            <p>
              Ailsa married businessman Bruce Fraser, a champion rower and past president of Rowing NZ, who joined the family foundry exporting firm AW Fraser, and used his global business expertise to assist trade missions. He is a Life Member of the Canterbury Employers' Chamber of Commerce, and set up the St Andrews College Foundation. Ailsa and Bruce combine philantrophy with commercial acumen.
            </p>
          </div>

          <p>
            Young Edward loved and excelled at primary school, which was an hour's horse and buggy trip each way. With his first prize money he bought a little Kodak box camera. He was later a member of Amuri Camera Club and had photography was a lifetime interest.
          </p>

          <p>
            In 1928 he boarded at Rangiora High School, then attended Christchurch Cathedral Grammar School, getting his matriculation to attend Canterbury College<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-9">9</a></sup> in 1936. He returned to farm on Riverside with his father but he was clearly more of a scholar than a farmer.
          </p>

          <p>
            Ten years after his father married Eva, Edward T. had his own bride. Edward had seen Amy Wooding<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-10">10</a></sup> on one of his trips to Marlborough to visit his Trueman relatives - and he decided she was the woman he wanted to marry. That happened in 1937, when he was 25 and she was 29, after Amy had moved south and taken a job as a nurse aid at Amuri Hospital, Rotherham.
          </p>

          {/* Image 211 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/211 Edward Roberts married Amty Wooding 1937.jpg"
              alt="Edward Roberts married Amy Wooding 1937"
              caption={`L-R: Mother of the bride Alice Amy Wooding¹¹, Edward and Amy Maud Roberts, Amy's father, Arthur Richard Wooding¹² at St Peters Ward, at the wedding conducted by Rev C Barker. Amy was the second daughter of Mr & Mrs Wooding of Te Moana, Ward. "The bride wore¹³ a trained frock of cream crepe cavalcade dress caught at the slightly cowled neckline with a spray of orange blossoms and clusters of similar blossoms, which had been worn by the bridegroom's mother, held in place by a veil of embroidered tulle. Her bouquet was Madonna lilies and roses. The bride's mother wore a frock of black silk serge trimmed with gold stitched braid, and a hat of black gramophone straw. Her flowers were mauve sweet pea and statice.... The bride travelled in a tailored suit of almond green linen, a beige silk blouse, and a chip straw hat with accessories to tone."`}
              filenameHint="211 Edward Roberts married Amty Wooding 1937.jpg"
            />
          </figure>

          <p>
            Edward and Amy, a music teacher, pianist and church organist, had four children.
          </p>

          {/* Image 212 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/212 Richard, Jan, Marc and Kevin Roberts.jpg"
              alt="Richard, Jan, Marc and Kevin Roberts"
              caption="L-R: Richard, Jan, Marc and Kevin, taken at the Finnerty-Leigh Studios, 4 New Regent Street Christchurch"
              filenameHint="212 Richard, Jan, Marc and Kevin Roberts.jpg"
            />
          </figure>

          <div className="space-y-2">
            <p>
              Richard had a wide ranging career as a teacher, ranging from country high schools, to the Solomon Islands, to the NZ Correspondence School.
            </p>
            <p>
              Marc became the farmer of his generation.
            </p>
            <p>
              Jan (Janette Lipscombe) had an early career in banking.
            </p>
            <p>
              Keven was a qualified carpenter, small farmer and Building Inspector for the Hurunui District Council he was able to say there were "no leaky buildings on my watch" referring to the liability caused to many homeowners due to poor quality materials, poor design and workmanship which resulted in buildings that were not weatherproof.
            </p>
          </div>

          <p>
            In 1968 Edw. T. took over Riverside when his father Edward retired to Kendal Avenue, Burnside, Christchurch. Edward died in 1975 and Eva died, aged 98, in 1996. They are buried at Culverden Cemetery.
          </p>

          {/* Image 213 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/213 Riverside in 1950.jpg"
              alt="Riverside in 1950"
              caption="Riverside in 1950"
              filenameHint="213 Riverside in 1950.jpg"
            />
          </figure>
        </section>

        {/* Section: RIVERSIDE --- over a century of change in farming practice */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <h2 className="font-serif text-2xl sm:text-3xl font-extrabold text-[#2c221e] tracking-tight">
            "RIVERSIDE" ---over a century of change in farming practice
          </h2>
          <p className="font-serif text-base font-bold text-[#7a5b3a]">
            by Richard Lovell Roberts
          </p>

          <p>
            Riverside is located on Mouse Point-Flintofts Road in the dry Amuri basin surrounded by hills and mountains. The Waiau River is its northern boundary.
          </p>

          <p>
            Under Edward sen., and then his son Edward T's tenure, Riverside like most of the family farms until the 1990s and the change to dairying, was a 'mixed farm' producing wool and fat lambs raised on pasture grass, lucerne, and root crops, and ultimately exported to Britain the main market after processing at 'freezing works' at Belfast and Islington near Christchurch. The baled wool went in the same direction.
          </p>

          <p>
            The idea of a common European market began to firm-up during the 1970s, and trends to globalisation would ultimately change practices. Until then, on Riverside, pasture grasses provided seed, and hay which was baled and stored for stock feeding in winter. Wheat was grown for flour production in companies located in Christchurch and other Canterbury centres. Its production and milling was phased out after the free trade agreement (CER) between NZ and Australia in March 1983. Up until WW2, oats were also grown for chaff-making to feed the draft horses, the main energy for operating farm equipment.
          </p>

          {/* Image 214 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/214 Horse team at Riverside 1930.jpg"
              alt="The horse team at Riverside 1930"
              caption="The horse team at Riverside 1930 (Photo: Ailsa Fraser)"
              filenameHint="214 Horse team at Riverside 1930.jpg"
            />
          </figure>

          <p>
            A Massey Harris tractor bought in the late 1930s, was not extensively used during wartime petrol rationing so horse teams still operated during the 1940s. Oats were cut by horse team and a Massey-Harris harvester-binder and stooks built into conical stacks in paddock corners awaited cutting into chaff which was bagged and stored in a loft directly above horse stalls in the shearing shed complex in the yard below Edward's house.
          </p>

          <p>
            A few steers were fattened for beef production, and about four 'house' cows were milked by hand daily for the 2 farm families. Some milk was 'separated' with a hand-turned Alfa-Laval Separator to produce cream, sent to a butter factory near Christchurch via NZ Road Services bus.
          </p>

          <p>
            A daily freight train from Christchurch via a branch line from Waipara came as far as Waiau Station. It off-loaded and picked up 'goods' there, and at Culverden Station and also at un-manned 'sidings' with a loading ramp and small named storage shed, and a side-line for a carriage or two. In Riverside's case the siding was called Achray. We loaded stock, wheat sacks, hay for consignment there. Sheep were a common sight on country roads, being driven by a farmer, his dogs, and young boys flapping arms and making a performance to impress motorists impeded by stock on the road especially after the annual stock sale at Culverden. Mocketts Transport, and later Amuri Transport (1965) also provided cartage, and especially after rail was abolished (1978) and track lifted.
          </p>

          <p>
            Edward Roberts had also established a couple of fruit orchards during his early years. These provided fruit for preserving and jam making. His brother Herbert John cared for these until 1932. In a corner opposite the sheep yards, pigs were kept and fed as a daily chore. Edward home-cured and smoked his bacon. He also planted pine tree shelterbelts for stock against summer heat, and winter southerlies. After ploughing for crops, the whole end of a paddock would be fenced-off for a vege garden providing potatoes, peas, and carrots in long rows which had to be ridged up, thinned, or weeded as the case may be. These provisioned the families and many more. Edward was a generous person and gave lots of produce to charities in Christchurch, as well as to people in need during the 1930s economic 'Depression'. Tending to the garden, helping in the woolshed and cowshed, harvesting, lambing, tailing, etc, was a weekend or school holiday chore for Edward T's and Amy's young sons as they grew.
          </p>

          <p>
            Throughout the 1960s and '70s increasing mechanisation in the district meant more production. More stock on farms in a naturally dry region with exacerbating droughts raised popular calls for irrigation schemes. It was supported by government and has effectively changed local farming practices.
          </p>

          <p>
            After Edward T. retired, his second son Marc and wife Jan began the conversion to dairying in 1997, and now in 2021, his second son Philip and wife Rona, with supporting labour, run a highly productive dairy farm of 800 cows which incorporates extra land bought when a neighbour sold out. Looking across today's farming landscape with its giant computerised wandering irrigators in place of shelter belts, the silos, milk tanks, long silver Fonterra tankers coming and going, and a dairy with rotating milking platforms instead of the old black-tarred cowshed with leg-ropes, buckets and stools that he used, the practices and the prices for its produce....all would seem incredible to Edward who won it in the 1908 ballot, and started 'from scratch'.
          </p>

          {/* Image 215 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/215 Riverside pre 1958.jpg"
              alt="Riverside pre-1968"
              caption="Riverside pre-1968 (Photo: David Fordyce)"
              filenameHint="215 Riverside pre 1958.jpg"
            />
          </figure>

          {/* Image 216 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/216 Ted milking Old Bluey 1959.jpg"
              alt="Ted milking Old Bluey 1959"
              caption="Ted Roberts (aged 78) milking Old Bluey in 1959, inspired his grandson Richard to write this poem 57 years later."
              filenameHint="216 Ted milking Old Bluey 1959.jpg"
            />
          </figure>

          {/* Poem */}
          <div className="bg-[#f3ede0] p-6 sm:p-8 rounded-2xl border-l-4 border-[#7a5b3a] font-serif text-sm sm:text-base leading-relaxed text-[#2c221e] italic space-y-2 whitespace-pre-line">
{`Off the school-bus, home
and into the cow shed
where homework begins,
helping 'grandpa' with a daily routine.
'Old Bluey' haltered, hay-blissed, brim-full, and placid,
wait our attention.
And he, hoar-headed, pail eased between arthritis and 'knees, reaches down to impart us,
the fine art of milking,
its deftness of touch, and its rhythm.
Adroitly alert, farm cats poised in dress-circle with children watching,
anticipate hands skipping a beat, to interrupt cadence
with a sly squeeze of a teat,
felicitously aimed at the jaw-gaping audience,
with an oh-so-lick-licious, whiskers and pawful,
jet-stream of delight!
Bluey and grandpa,
how you humoured and succoured
our childhood's small realm,
in that old timber-and-tarred
cobwebby cowshed, hung on the world's edge¹⁴`}
          </div>

          {/* Image 217 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/217  Edward T. Robert_s Coat of Arms.jpg"
              alt="Edward T.'s Coat of Arms"
              caption="Edward T.'s Coat of Arms: Motto &quot;While I breathe I hope&quot; The Supporters are Dexter, a Crusader Knight of St Lazarus of mid-13C, and a lady in Welsh traditional dress holding a leather-thronged purse."
              filenameHint="217  Edward T. Robert_s Coat of Arms.jpg"
            />
          </figure>

          <div className="space-y-4">
            <h3 className="font-serif text-xl font-bold text-[#2c221e]">
              A life that defined community service
            </h3>
            <p className="font-medium text-[#4a3525]">
              Some of the positions and involvements he held:
            </p>
            <ul className="space-y-1.5 pl-2 sm:pl-4 border-l-2 border-[#d8ceb9] my-3 text-sm sm:text-base text-[#2c221e]">
              <li>• Chair and Patron of the Amuri Historic Society</li>
              <li>• Life Member, Society of Genealogists, London - he loved the pagaentry</li>
              <li>• Patron, NZ Family History Association</li>
              <li>• Masonic and Manchester Unity Lodges from the 1930s</li>
              <li>• Fellow Royal Geographic Society</li>
              <li>• Secretary NZ Farmers' Union Culverden branch 1938-45</li>
              <li>• Secretary Amuri A & P Association 1938-46</li>
              <li>• Home Guard WW2</li>
              <li>• Forest & Bird Protection Society membership (as too, was his father, Edward)</li>
            </ul>
          </div>

          <p>
            He was a founder member of The Order of St John in Culverden, helping with some other locals, to establish an Ambulance Sub-Centre there to serve the district, including the Lewis Pass. He was Deputy Chairman of Canterbury-West Coast Centre 1963-7. He was long involved in the Leprosy Mission in NZ<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-15">15</a></sup>. That in turn led to his involvement in the medical charity, the Order of St Lazarus of Jerusalem and he was involved with founding the NZ Branch of the Order in 1962. One of the most ancient of the European Orders of Chivalry, dating back to the Crusader Knights, and is dedicated to those suffering from leprosy and to defending the Christian faith. He represented NZ at the International Meeting of the Order in France in 966. He was Vice Chancellor of the Grand Bailiwick of NZ in 1970 and received the 20-year Meritorious Service Decoration. He was also a licensed Lay Reader, and Synod representative in the (Anglican) Nelson Diocese.
          </p>

          <p>
            His wife Amy was a Dame within the Order, and his sister Margaret was also involved.
          </p>

          <p>
            Ted had the family tree recorded at the College of Arms, and left records that linked generations, although not necessarily in readily accessible formats.
          </p>

          {/* Image 218 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/218 Part of family tree researched by Edward T.jpg"
              alt="Part of family tree researched by Edward T."
              caption="Example of part of a family tree researched by Edw. T."
              filenameHint="218 Part of family tree researched by Edward T.jpg"
            />
          </figure>

          {/* Image 219 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/219 Ted and Tom Roberts.jpg"
              alt="Ted and Tom Roberts"
              caption="Ted Roberts(I), and his brother, farmer, writer, and poet, T.E.L. Roberts. (Photos: Ailsa Fraser)"
              filenameHint="219 Ted and Tom Roberts.jpg"
            />
          </figure>

          {/* Image 220 */}
          <figure className="my-6 sm:my-8">
            <ArchivalImageCard
              src="public/images/220 Newspaper cutting about trip to Wales.jpg"
              alt="Newspaper cutting about trip to Wales"
              caption="Newspaper cutting about trip to Wales"
              filenameHint="220 Newspaper cutting about trip to Wales.jpg"
            />
          </figure>

          <p className="text-xs text-[#7a5b3a] italic">
            Note: Andrews & Beaven was established in 1878 as agricultural engineers by William Andrews and Arthur Ward Beaven.
          </p>
        </section>

        {/* Section: THIS LETTER WAS WRITTEN SHORTLY AFTER TED'S WIFE DIED */}
        <section className="space-y-6 pt-6 border-t-2 border-[#d8ceb9]">
          <div className="flex items-center gap-2 text-[#7a5b3a]">
            <Mail className="w-5 h-5" />
            <h2 className="font-serif text-xl sm:text-2xl font-extrabold text-[#2c221e] tracking-tight">
              THIS LETTER WAS WRITTEN SHORTLY AFTER TED'S WIFE DIED AFTER CHILDBORTH. THE BABY STAYED IN THE MATERNITY HOSPITAL AND MAGGIE WAS LATER TO LIVE WITH TED AND HELP RAISE YOUNG EDWARD FOR 15 YEARS
            </h2>
          </div>

          <blockquote className="bg-[#f3ede0] p-6 sm:p-8 md:p-10 rounded-2xl border-l-4 border-[#7a5b3a] space-y-4 font-serif text-base leading-relaxed text-[#2c221e]">
            <p className="font-bold text-right text-sm text-[#7a5b3a]">
              Culverden<br />April 22nd 1913
            </p>

            <p className="font-bold">
              Dear Maggie
            </p>

            <p>
              I thought I would write you a few lines tonight as I know you will be pleased to hear how we are. Mrs Gallagher made us go in for dinner on our way home and on Sunday morning we went to church and Mrs Popplewell<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-16">16</a></sup> asked us to come in and have dinner with them on our way back.
            </p>

            <p>
              Mr Brae had a memorial service and if you have a Church of England hymn book or look in the back of the prayer book you can find the hymns 286, 221, and 499. They are lovely hymns.
            </p>

            <p>
              How is mother keeping? I hope you will soon have her home with you again. I think she will improve more quickly when she is back, and Maggie I wonder how my little boy is, any time when you want a run out I know Mrs Bethane<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-17">17</a></sup> would not mind you calling to see him. She said she might want a shawl for him. I told her she could get it at Graham Wilson's or any other little thing he may need.
            </p>

            <p>
              I had such a kind letter from Mr Billens today. He said he had been round to see Father.
            </p>

            <p>
              I was in Culverden today trucking some old ewes and lambs to Addington and saw poor old Jim Gallagher there. He was greatly upset. His heart I think is like yours, so large but so soft.
            </p>

            <p>
              George Wilke our quiet man came back tonight. I was pleased to see him as there is a lot of work on hand and I must get to it. I just read in the paper tonight that Rider Haggard thought young men would be better employed farming than writing books and I believe he is right.
            </p>

            <p>
              Annie<sup className="font-bold text-[#7a5b3a] hover:underline cursor-pointer"><a href="#fn-p3-18">18</a></sup> is managing very well and is a comfort to me. She is never down hearted but always even. It is about bedtime so will close with love to Father and Mother and yourself.
            </p>

            <p className="font-bold">
              From Ted
            </p>
          </blockquote>

          <div className="space-y-4">
            <h3 className="font-serif text-xl font-bold text-[#2c221e]">
              EDWARD T. ROBERTS GOT A STEPMOTHER - report on the community celebration for the wedding of Ted and Eva Fanny Rinaldi, and a report on the wedding, October 1928
            </h3>

            {/* Image 221 */}
            <figure className="my-6 sm:my-8">
              <ArchivalImageCard
                src="public/images/221 Newspaper cutting about a surprise party.jpg"
                alt="Newspaper cutting about a surprise party"
                caption="Newspaper cutting about a surprise party"
                filenameHint="221 Newspaper cutting about a surprise party.jpg"
              />
            </figure>
          </div>
        </section>

        {/* FOOTNOTES BLOCK */}
        <section className="pt-8 border-t-2 border-[#d8ceb9] space-y-3 text-xs text-[#5c4328]">
          <div className="flex items-center gap-1.5 font-serif font-bold text-sm text-[#7a5b3a] mb-2">
            <Landmark className="w-4 h-4" />
            <span>Footnotes &amp; Archival Citations (Part 3)</span>
          </div>

          <ol className="space-y-1.5 pl-5 list-decimal">
            <li id="fn-p3-1">
              Edward Roberts, 1881-1975, was the son R.M. &amp; S. E. Roberts, the seventh of 12 children
            </li>
            <li id="fn-p3-2">
              Sarah, 1878-1913, the daughter of Robert and Sarah (nee Blackmore) Trueman of Marlborough
            </li>
            <li id="fn-p3-3">
              Quotes taken from "A Personal Memoir"
            </li>
            <li id="fn-p3-4">
              Ted had spent some time in Southland, at Morton Mains earlier, with a view to farming there, but decided to return north.
            </li>
            <li id="fn-p3-5">
              Riverside at Culverden
            </li>
            <li id="fn-p3-6">
              Stanley was married to Edward's cousin Margaret Pain. He worked for Andrews and Beaven.
            </li>
            <li id="fn-p3-7">
              Block 20 Plot 170 Linwood Cemetery
            </li>
            <li id="fn-p3-8">
              Eva born 1898 at West Eyreton
            </li>
            <li id="fn-p3-9">
              Later named University of Canterbury
            </li>
            <li id="fn-p3-10">
              1909-1990
            </li>
            <li id="fn-p3-11">
              Née Andrews, 1881-1938
            </li>
            <li id="fn-p3-12">
              1882-1950
            </li>
            <li id="fn-p3-13">
              Reported in The Press 4 January 1938
            </li>
            <li id="fn-p3-14">
              From "Operetta from Childhood - memories of a grandfather" by Richard Roberts, 2016
            </li>
            <li id="fn-p3-15">
              His cousin Annie Denton's son Harry left a considerable legacy to this organisation
            </li>
            <li id="fn-p3-16">
              The vicar's wife, Mrs Popplewell. She and her husband, George had the adjoining farm on Riverside's eastern boundary. He would have passed their gate on the way back from church at Rotherham. The Gallagher farm was on Riverside's western boundary.
            </li>
            <li id="fn-p3-17">
              Mrs Bethane ran the maternity hospital
            </li>
            <li id="fn-p3-18">
              Annie Gibb, Ted's niece
            </li>
          </ol>
        </section>

        {/* Bottom Pagination / Series Navigation */}
        <div className="pt-8 flex flex-wrap items-center justify-between gap-4 border-t-2 border-[#d8ceb9]">
          {onSelectPage && (
            <button
              onClick={() => onSelectPage('part2')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#7a5b3a] font-serif font-bold text-xs transition cursor-pointer"
            >
              <span>&larr; Part 2: Literate and Hardworking</span>
            </button>
          )}

          {onSelectPage && (
            <button
              onClick={() => onSelectPage('all')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#7a5b3a] hover:bg-[#5c4328] text-white font-serif font-bold text-xs transition cursor-pointer shadow-xs"
            >
              <span>View Table of Contents</span>
            </button>
          )}

          {onSelectPage && (
            <button
              onClick={() => onSelectPage('part4')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#e8dec9] hover:bg-[#d8ceb9] text-[#7a5b3a] font-serif font-bold text-xs transition cursor-pointer"
            >
              <span>Part 4: Personal Histories &rarr;</span>
            </button>
          )}
        </div>

      </article>
    </div>
  );
};
